import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'

import { RootStateType } from '@store/store'
import {
  DropDownOption,
  ExtendedUnitFilterOptions,
  ProjectIdentity,
} from '@store/types'

import DropDown from '@components/dropdown/v2'
import FILTER_INITIAL_STATE from '@components/filter/v2/filterState'
import ToggleSwitch from '@components/toggle-switch'

import { BuildingInterface, Level } from '@api/building'
import { MappingBlockCollection } from '@api/interactive-plan'
import { Filter } from '@api/white-label'

import mapWhiteLabelsWithDefaults from '@utilities/white-label'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { CloseSvg } from '@svg/react'

import {
  Unit,
  UnitFilterOptionsConfigurations,
} from '@adUtilities/types/apartment'
import { MinMaxInterface } from '@adUtilities/types/common'

import { DEFAULT_ANY_OPTION } from '../constants'
import { getInitialConfigurations, getInitialRange } from './constants'
import RangeOptions from './range-options'

export interface ConfigPopupProps {
  projectIdentity: ProjectIdentity
  filterWhiteLabel: Filter
  isOpen: boolean
  building: BuildingInterface
  blocks?: MappingBlockCollection
  firebaseUnitFilter?: ExtendedUnitFilterOptions
  firebaseActiveBlock: string
  firebaseActiveLevel?: string
  hideLevelOption?: boolean
  toggle: (arg: boolean) => void
  filterStyle?: string
}

function BuildingFilter({
  projectIdentity,
  filterWhiteLabel,
  isOpen,
  building,
  blocks,
  firebaseUnitFilter,
  firebaseActiveBlock,
  firebaseActiveLevel,
  hideLevelOption,
  toggle,
  filterStyle = 'fixed top-[36px] z-30 h-page-container rounded-tl-2xl',
}: ConfigPopupProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const {
    prices,
    showPrice,
    propertyConfig,
    aspects,
    unitTypes,
    statusLabels,
  } = projectIdentity || {}

  const filterRef = useRef<HTMLDivElement>(null)

  const [showAvailable, setShowAvailable] = useState<boolean>(
    Boolean(firebaseUnitFilter?.showAvailable)
  )
  const [checkAnyLevel, setAnyLevel] = useState<boolean>(
    Boolean(firebaseUnitFilter?.anyLevel)
  )
  const [configurations, setConfigurations] =
    React.useState<UnitFilterOptionsConfigurations>(
      getInitialConfigurations(firebaseUnitFilter)
    )
  const [range, setRange] = useState<MinMaxInterface>(
    getInitialRange(firebaseUnitFilter)
  )
  const [aspect, setAspect] = useState<string[]>(
    firebaseUnitFilter?.aspect || []
  )
  const [filterUnitType, setFilterUnitType] = useState<string>(
    firebaseUnitFilter?.unitType || ''
  )

  const [activeLevel, setActiveLevel] = useState<string>(
    firebaseActiveLevel || ''
  )

  const unitTypeOptions = useMemo(
    () =>
      unitTypes?.map((item) => ({
        label: item,
        value: item,
      })),
    [unitTypes]
  )

  const getLevelOptions = React.useMemo(() => {
    const levelList: Level[] = [...building.levels]

    const filteredLevels = levelList.filter((lvl) =>
      showAvailable
        ? lvl.data.some(
            (unit: Unit) => unit.metas.status === statusLabels?.available
          )
        : true
    )

    const levels: Array<DropDownOption> = []
    filteredLevels.forEach((res) => {
      if (firebaseActiveBlock && Object.keys(blocks || {}).length > 1) {
        if (res.data.find((lvl) => lvl.blockId === firebaseActiveBlock)) {
          levels.push({
            label: res.level,
            value: res.level,
          })
        }
      } else {
        levels.push({
          label: res.level,
          value: res.level,
        })
      }
    })

    return levels
  }, [building.levels, showAvailable, statusLabels, firebaseActiveBlock])

  const handleRangeChange = async (rangeValue: MinMaxInterface) => {
    await firebaseControlQuery.update({
      [`building.unitFilter.range`]: rangeValue,
      [`building.unitFilter.apply`]: false,
    })
  }

  const handleAspectOnChange = async (value: string) => {
    if (value === DEFAULT_ANY_OPTION) {
      await firebaseControlQuery.update({
        [`building.unitFilter.aspect`]: [],
      })
      return
    }

    if (aspect.includes(value)) {
      await firebaseControlQuery.update({
        [`building.unitFilter.aspect`]: aspect.filter((item) => item !== value),
        [`building.unitFilter.apply`]: false,
      })
    } else {
      await firebaseControlQuery.update({
        [`building.unitFilter.aspect`]: [...aspect, value],
        [`building.unitFilter.apply`]: false,
      })
    }
  }

  const handleUnitTypeOnchange = async (value: string) => {
    await firebaseControlQuery.update({
      [`building.unitFilter.unitType`]: value,
      [`building.unitFilter.apply`]: false,
    })
  }

  const handleConfigurationOnchange = async (
    configs: UnitFilterOptionsConfigurations
  ) => {
    await firebaseControlQuery.update({
      [`building.unitFilter.configurations`]: configs,
      [`building.unitFilter.apply`]: false,
    })
  }

  const handleShowAvailable = async (checked: boolean) => {
    await firebaseControlQuery.update({
      [`building.unitFilter.showAvailable`]: checked,
      [`building.unitFilter.apply`]: false,
    })
  }

  const handleLevelOnChange = async (level: string) => {
    await firebaseControlQuery.update({
      [`building.activeLevel`]: level,
      [`building.activeUnit`]: '',
    })
  }

  const handleAnyLevel = async (checked: boolean) => {
    await firebaseControlQuery.update({
      [`building.unitFilter.anyLevel`]: checked,
      [`building.unitFilter.apply`]: false,
    })
  }

  const applyFilter = async () => {
    toggle(false)

    const filterPayload = {
      'building.unitFilter.apply': true,
      ...(!hideLevelOption && { 'building.activeLevel': activeLevel }),
      'building.activeUnit': '',
    }

    await firebaseControlQuery.update(filterPayload)

    handleLevelOnChange(activeLevel)
  }

  const resetFilter = async () => {
    await firebaseControlQuery.update({
      [`building.unitFilter`]: FILTER_INITIAL_STATE,
    })
  }

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      isOpen &&
      filterRef.current &&
      !filterRef.current.contains(event.target as Node)
    ) {
      toggle(false)
    }
  }

  useEffect(() => {
    setRange(getInitialRange(firebaseUnitFilter))
    setAspect(firebaseUnitFilter?.aspect || [])
    setAnyLevel(Boolean(firebaseUnitFilter?.anyLevel))
    setFilterUnitType(firebaseUnitFilter?.unitType || '')
    setShowAvailable(Boolean(firebaseUnitFilter?.showAvailable))
    setConfigurations(getInitialConfigurations(firebaseUnitFilter))
  }, [firebaseUnitFilter])

  useEffect(() => {
    if (isOpen && firebaseUnitFilter?.apply) {
      firebaseControlQuery.update({
        [`building.unitFilter.apply`]: false,
      })
    }
  }, [activeLevel])

  useEffect(() => {
    setActiveLevel(firebaseActiveLevel || '')
  }, [firebaseActiveLevel])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleOutsideClick, false)
    }, 0)
    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [isOpen])

  return (
    <div
      ref={filterRef}
      className={`${filterStyle} w-[297px] bg-white px-4 pb-10 text-black shadow-2xl ${
        isOpen ? '' : '-translate-x-full'
      } transition-transform duration-500`}
    >
      <div className="flex h-screen flex-col">
        <div className="mb-2 mt-6 flex items-center justify-between">
          <div>
            <button
              className="flex items-center"
              type="button"
              onClick={() => toggle(!isOpen)}
            >
              <CloseSvg
                className="h-3.5 w-3.5 cursor-pointer"
                strokeColor="black"
              />
            </button>
          </div>
          <div>
            <h4 className="text-xl font-semibold">Filter</h4>
          </div>
          <div>
            <button
              type="button"
              onClick={resetFilter}
              className="text-base font-medium text-mainColour underline"
            >
              Reset
            </button>
          </div>
        </div>
        <div className="no-scrollbar h-[84%] overflow-y-auto">
          <div className="mt-6 flex flex-col gap-[1.20rem] text-base font-medium">
            {getLevelOptions.length && !hideLevelOption ? (
              <div className="flex flex-col gap-1">
                <span className={` ${checkAnyLevel ? 'text-gray-300' : ''}`}>
                  {filterWhiteLabel.levelDropdownLabel}
                </span>
                <DropDown
                  onSelect={(value) => {
                    setActiveLevel(value)
                  }}
                  items={getLevelOptions}
                  disabled={checkAnyLevel}
                  value={activeLevel}
                  className="py-2"
                  buttonRadius="rounded-[7px]"
                  buttonRadiusClasses="rounded-tl-[7px] rounded-tr-[7px]"
                  optionRadiusClasses="rounded-bl-[7px] rounded-br-[7px]"
                />

                <div className="mt-2 inline-flex items-center">
                  <ToggleSwitch
                    key="anyLevel"
                    id="anyLevel"
                    onChangeCallback={(e) => {
                      handleAnyLevel(e.target.checked)
                    }}
                    initialState={checkAnyLevel}
                    onColor="bg-mainColour"
                    offColor="bg-[#BDC3C7]"
                  />
                  <div className="ml-3 text-base font-medium">Any Level</div>
                </div>
              </div>
            ) : (
              ''
            )}

            {showPrice && (
              <RangeOptions
                field="price"
                options={prices}
                range={range}
                handleChange={handleRangeChange}
              />
            )}

            {Object.entries(propertyConfig)?.map(
              ([field, value]) =>
                value?.length > 0 && (
                  <RangeOptions
                    field={field}
                    key={field}
                    options={value as unknown as Array<string | number>}
                    range={
                      configurations[
                        field as keyof UnitFilterOptionsConfigurations
                      ]
                    }
                    handleChange={(configValue: MinMaxInterface) => {
                      handleConfigurationOnchange({
                        ...configurations,
                        [field as keyof UnitFilterOptionsConfigurations]:
                          configValue,
                      })
                    }}
                  />
                )
            )}

            {unitTypeOptions.length > 0 && (
              <div className="flex flex-col gap-1">
                <span>Unit Types</span>
                <DropDown
                  onSelect={handleUnitTypeOnchange}
                  items={[
                    { value: DEFAULT_ANY_OPTION, label: DEFAULT_ANY_OPTION },
                    ...unitTypeOptions,
                  ]}
                  value={filterUnitType}
                  className="py-2"
                  buttonRadius="rounded-[7px]"
                  buttonRadiusClasses="rounded-tl-[7px] rounded-tr-[7px]"
                  optionRadiusClasses="rounded-bl-[7px] rounded-br-[7px]"
                />
              </div>
            )}

            {aspects && aspects.length > 0 && (
              <div className="flex flex-col gap-1">
                <span>Aspect</span>
                <DropDown
                  onSelect={(item) => handleAspectOnChange(item)}
                  items={[
                    { value: DEFAULT_ANY_OPTION, label: DEFAULT_ANY_OPTION },
                    ...aspects.map((item) => ({
                      value: item,
                      label: item,
                    })),
                  ]}
                  value={
                    aspect.length === 0 ? DEFAULT_ANY_OPTION : aspect.join(', ')
                  }
                  className="break-all py-2"
                  buttonRadius="rounded-[7px]"
                  buttonRadiusClasses="rounded-tl-[7px] rounded-tr-[7px]"
                  optionRadiusClasses="rounded-bl-[7px] rounded-br-[7px]"
                />
              </div>
            )}
          </div>
        </div>
        <div className="mb-12 flex flex-col gap-[1.20rem]">
          <div className="mt-2 inline-flex items-center whitespace-nowrap">
            <ToggleSwitch
              key="availableResidence"
              id="availableResidence"
              onChangeCallback={(e) => {
                handleShowAvailable(e.target.checked)
              }}
              initialState={showAvailable}
              onColor="bg-mainColour"
              offColor="bg-[#BDC3C7]"
            />
            <div className="ml-3 text-base font-medium">
              {filterWhiteLabel.toggleText}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={applyFilter}
              disabled={firebaseUnitFilter?.apply}
              className={`rounded-lg px-4 py-4 text-xl font-semibold text-white duration-300 ${
                firebaseUnitFilter?.apply ? 'bg-gray-300' : 'bg-mainColour'
              }`}
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default connect(({ projectIdentity, whiteLabel }: RootStateType) => ({
  projectIdentity,
  filterWhiteLabel: mapWhiteLabelsWithDefaults(whiteLabel).showcase.filter,
}))(BuildingFilter)
